// ---------------------------------------------------------------------------
// Connecting a sending account, assigning it, warming it up (R18b, from pages/settings.ts)
//
// A mailbox (Gmail or Outlook through Unipile, ADR-0001) and a LinkedIn
// account connect the same way: a popup + poll.
//
//   1. The sign-in popup opens INSIDE the click, before anything is awaited.
//      A browser lets a page open a window only while it holds the click's
//      user activation; the round trip to our server for the provider's URL
//      outlives it, and a window.open() after that await is blocked
//      (oauth-popup.ts). So connectMailbox() and connectLinkedin() are not
//      async: they open the popup, then return the promise of the rest.
//   2. The server is asked for the provider's sign-in URL and the popup is
//      sent there (or, when the browser blocked it, the person gets a link).
//   3. The server's status is polled — every 2 s for a mailbox, every 3 s for
//      LinkedIn — until the popup closes or the server reports the flow done,
//      and for five minutes at most.
//   4. Then ONLY the accounts the server names as connected in this flow
//      (`connectedAccountIds`) are assigned to the rep. Never every unassigned
//      account: SDR 2.1 and 2.0 share one Unipile workspace, and a mailbox
//      left unassigned by a deleted rep, or connected from 2.0, would be swept
//      onto this rep. Bulk-assigning once put one rep's inbox on another.
//
// `completed` counts as done too: a reconnect of an account Unipile already
// held files nothing new (email-connect-outcome.ts).
// ---------------------------------------------------------------------------

import type { AuthPopup } from '../oauth-popup.js';
import { emailConnectFlowEnded, emailConnectSucceeded } from './email-connect-outcome.js';
import { answerOf, browserTimers, type Say, type Send, type Timers, viaFetch, warn } from './settings-flow.js';
import { warmupToast } from './settings-safeguards.js';

/** How often each flow asks the server, and when it gives up. */
export const POLL_MS = { email: 2000, linkedin: 3000 } as const;
export const GIVE_UP_MS = 5 * 60 * 1000;

/** How long a poll may wait for the server before the next one goes anyway. */
export const POLL_STALE_MS = 30_000;

/**
 * One flow's polls, one at a time (R18b). setInterval does not wait for an
 * async callback: when the status route answers slower than the interval (it
 * asks Unipile for its account list first), the next poll started while the
 * last still waited, both could come back "done" — the server's check is not
 * atomic either — and both assigned: the mailbox twice, and from the editor
 * of a new rep, the rep created twice. So a poll does not start while the
 * last one waits, unless that one has waited POLL_STALE_MS (a request that
 * hangs must not stop the flow), and only the first poll that ends the flow
 * acts on it. A skipped poll is said once per flow in the console.
 */
function pollGuard(flow: keyof typeof POLL_MS, timers: Timers) {
  let since: number | null = null;
  let said = false;
  let ended = false;
  return {
    /** Whether this poll may ask the server now. */
    start(): boolean {
      if (ended) return false;
      if (since !== null && timers.now() - since < POLL_STALE_MS) {
        if (!said) {
          said = true;
          warn('connect poll skipped: the previous status request is still waiting for the server', {
            flow,
            everyMs: POLL_MS[flow],
          });
        }
        return false;
      }
      since = timers.now();
      return true;
    },
    /** This poll has its answer (or failed). */
    stop(): void {
      since = null;
    },
    /** This poll ends the flow; false when another one already did, and this one must do nothing. */
    end(): boolean {
      if (ended) return false;
      ended = true;
      return true;
    },
  };
}

/**
 * The rep a freshly connected account goes to: a concrete id, or a resolver
 * that creates or returns one. The resolver runs only AFTER the sign-in
 * succeeded (just before the assignment), so the popup opens with the same
 * latency as before and no rep is created when the person abandons the flow.
 */
export type RepIdTarget = string | (() => Promise<string | null>);

export async function resolveRepId(target: RepIdTarget | undefined): Promise<string | null> {
  if (target == null) return null;
  return typeof target === 'function' ? await target() : target;
}

export interface AccountDeps {
  send?: Send;
  say: Say;
  /** Reload the page's lists (not awaited). */
  reload(): Promise<void>;
}

/** Assigns one account to a rep (PUT /api/accounts/:provider/:accountId/assign) and reloads. */
export async function assignAccount(
  provider: string,
  accountId: string,
  aiEmployeeId: string | null,
  deps: AccountDeps,
): Promise<void> {
  const send = deps.send ?? viaFetch;
  try {
    const res = await send(`/api/accounts/${provider}/${encodeURIComponent(accountId)}/assign`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aiEmployeeId }),
    });
    const data = await res.json();
    if (data.ok) {
      deps.say.success('Account assignment updated.');
      void deps.reload();
    } else {
      deps.say.error(data.error ?? 'Failed to assign account');
    }
  } catch {
    deps.say.error('Failed to assign account');
  }
}

/** Where each kind of account is listed and assigned. */
const KIND = {
  email: { list: '/api/accounts/unipile-email', provider: 'unipile_email' },
  linkedin: { list: '/api/accounts/linkedin', provider: 'unipile_linkedin' },
} as const;

/**
 * Assigns ONLY the accounts the server named as connected in this flow, and
 * only those not already on the rep. Returns how many it assigned. The rep is
 * resolved only when there is something to assign.
 */
export async function assignJustConnected(
  kind: keyof typeof KIND,
  connectedIds: string[],
  target: RepIdTarget | undefined,
  deps: AccountDeps,
): Promise<number> {
  if (connectedIds.length === 0) return 0;
  const repId = await resolveRepId(target);
  if (!repId) return 0;
  const listed = await answerOf((deps.send ?? viaFetch)(KIND[kind].list));
  if (!listed.ok) return 0;
  const justConnected = new Set(connectedIds);
  let assigned = 0;
  for (const acc of listed.accounts ?? []) {
    // Only a just-connected account that is not already on this rep.
    if (justConnected.has(acc.id) && acc.aiEmployeeId !== repId) {
      await assignAccount(KIND[kind].provider, acc.id, repId, deps);
      assigned++;
    }
  }
  return assigned;
}

export interface ConnectDeps extends AccountDeps {
  /** openAuthPopup (oauth-popup.ts), or a stand-in. */
  openPopup(opts: { message?: string; width?: number; height?: number }): AuthPopup;
  timers?: Timers;
  setConnecting(busy: boolean): void;
  /** A connect flow starts with a clean slate: the box above the panels is cleared. */
  clearMessages(): void;
}

/** The ids a status names as connected in this flow. */
const connectedIdsOf = (status: { connectedAccountIds?: unknown }): string[] =>
  Array.isArray(status.connectedAccountIds) ? status.connectedAccountIds : [];

/**
 * Connects a mailbox through Unipile and assigns it to `target`. Opens the
 * popup before it returns: call it straight from the click.
 *
 * `emailProvider` selects Gmail or Outlook and is carried through both the
 * init and the status poll — the server filters the provider's accounts by
 * type, so a poll for the wrong provider would never see the new mailbox.
 */
export function connectMailbox(
  target: RepIdTarget | undefined,
  emailProvider: 'google' | 'microsoft',
  deps: ConnectDeps,
): Promise<void> {
  deps.clearMessages();
  deps.setConnecting(true);
  // Opened before the first await, while the click's user activation is still ours.
  const popup = deps.openPopup({ message: 'Connecting your Sending Account…' });
  return pollMailbox(popup, target, emailProvider, deps);
}

async function pollMailbox(
  popup: AuthPopup,
  target: RepIdTarget | undefined,
  emailProvider: 'google' | 'microsoft',
  deps: ConnectDeps,
): Promise<void> {
  const send = deps.send ?? viaFetch;
  const timers = deps.timers ?? browserTimers;
  try {
    const initRes = await send('/api/connect/unipile-email/init', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailProvider }),
    });
    const initData = await initRes.json();
    if (!initData.ok) {
      popup.close();
      deps.say.error(initData.error ?? 'Failed to start mailbox connection');
      deps.setConnecting(false);
      return;
    }

    if (!(await popup.navigate(initData.url))) {
      deps.setConnecting(false);
      return;
    }

    const guard = pollGuard('email', timers);
    const poll = timers.setInterval(async () => {
      if (!guard.start()) return;
      try {
        const closed = popup.isClosed();
        const statusRes = await send(`/api/connect/unipile-email/status?emailProvider=${emailProvider}`);
        const statusData = await statusRes.json();

        // Keep polling until the popup closes or the server reports the flow
        // finished (a new mailbox filed, or a reconnect of one Unipile held).
        if (!emailConnectFlowEnded(closed, statusData)) return;
        if (!guard.end()) return;

        timers.clearInterval(poll);
        timers.clearTimeout(giveUp);
        if (!closed) popup.close();
        deps.setConnecting(false);

        const assigned = await assignJustConnected('email', connectedIdsOf(statusData), target, deps);
        void deps.reload();
        if (emailConnectSucceeded(statusData, assigned)) {
          deps.say.success('Sending Account connected!');
        }
      } catch {
        // A single failed poll is not a failed connection — the next one
        // answers. The popup closing is what ends this either way.
      } finally {
        guard.stop();
      }
    }, POLL_MS.email);

    // A popup left open otherwise polls every two seconds forever.
    const giveUp = timers.setTimeout(() => {
      timers.clearInterval(poll);
      deps.setConnecting(false);
    }, GIVE_UP_MS);
  } catch {
    popup.close();
    deps.say.error('Failed to connect Sending Account');
    deps.setConnecting(false);
  }
}

/** A LinkedIn status poll's answer. */
interface LinkedinStatus {
  ok?: boolean;
  newAccountsFound?: number;
  completed?: boolean;
  connectedAccountIds?: string[];
}

/**
 * Connects a LinkedIn account through Unipile and assigns it to `target`.
 * Opens the popup before it returns: call it straight from the click.
 */
export function connectLinkedin(target: RepIdTarget | undefined, deps: ConnectDeps): Promise<void> {
  deps.clearMessages();
  deps.setConnecting(true);
  // Opened before the first await — see connectMailbox.
  const popup = deps.openPopup({ message: 'Connecting your LinkedIn account…' });
  return pollLinkedin(popup, target, deps);
}

async function pollLinkedin(popup: AuthPopup, target: RepIdTarget | undefined, deps: ConnectDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  const timers = deps.timers ?? browserTimers;
  try {
    const initRes = await send('/api/connect/linkedin/init', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const initData = await initRes.json();
    if (!initData.ok) {
      popup.close();
      deps.say.error(initData.error ?? 'Failed to start LinkedIn connection');
      deps.setConnecting(false);
      return;
    }

    if (!(await popup.navigate(initData.url))) {
      deps.setConnecting(false);
      return;
    }

    // Whether the flow is done — a new account filed, or a reconnect the server
    // completed (which files nothing new, since a reconnected account is the
    // same person under a new provider id).
    const isDone = (statusData: LinkedinStatus) =>
      !!statusData.ok && ((statusData.newAccountsFound ?? 0) > 0 || statusData.completed === true);

    // Assigns the resolved account and announces success.
    const finishConnected = async (statusData: LinkedinStatus): Promise<void> => {
      const assigned = await assignJustConnected('linkedin', connectedIdsOf(statusData), target, deps);
      if ((statusData.newAccountsFound ?? 0) > 0 || statusData.completed === true || assigned > 0) {
        deps.say.success('LinkedIn account connected!');
      }
      void deps.reload();
    };

    const guard = pollGuard('linkedin', timers);
    const poll = timers.setInterval(async () => {
      if (!guard.start()) return;
      try {
        const closed = popup.isClosed();
        const statusRes = await send('/api/connect/linkedin/status');
        const statusData = await statusRes.json();
        const done = isDone(statusData);

        if (!closed && !done) return;
        if (!guard.end()) return;
        if (done) await finishConnected(statusData);

        timers.clearInterval(poll);
        timers.clearTimeout(giveUp);
        if (!closed) popup.close();
        deps.setConnecting(false);
      } catch {
        /* A single failed poll is not a failed connection — the next answers. */
      } finally {
        guard.stop();
      }
    }, POLL_MS.linkedin);

    const giveUp = timers.setTimeout(() => {
      timers.clearInterval(poll);
      deps.setConnecting(false);
    }, GIVE_UP_MS);
  } catch (err) {
    popup.close();
    deps.say.error(err instanceof Error ? err.message : 'Connection failed');
    deps.setConnecting(false);
  }
}

/**
 * Marks a Unipile mailbox already-warmed, or undoes it. Warming skips the
 * 15→35→75 daily ramp and sends at the full limit, and the server also
 * releases any of this mailbox's mail the old daily cap had pushed to
 * tomorrow. The lists are reloaded either way, so the switch shows the stored
 * state rather than an optimistic guess.
 */
export async function setMailboxWarmed(accountId: string, warmed: boolean, deps: AccountDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  try {
    const res = await send(`/api/accounts/unipile-email/${encodeURIComponent(accountId)}/warmup`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ warmed }),
    });
    const data = await res.json();
    if (data.ok) {
      deps.say.success(warmupToast(warmed, data));
    } else {
      deps.say.error(data.error ?? 'Failed to update the warm-up setting');
    }
  } catch {
    deps.say.error('Failed to update the warm-up setting');
  }
  void deps.reload();
}
